import React from "react";
import TrafficSources from "./TrafficSources";
import PopularSearches from "./PopularSearches";
import WebsiteUsageSummary from "./WebsiteUsageSummary";
import WebsiteTrendsChart from "./WebsiteTrendsChart";
import EmptyClassSearches from "./EmptyClassSearches";

const formatPercent = (numerator, denominator) => {
  if (!denominator) return null;
  return Math.round((numerator / denominator) * 100);
};

const buildQuickSummary = (totals, filterUsage, capacity, days) => {
  const lines = [];
  const summary = capacity?.summary || {};
  const programViews = totals.programClicks || 0;
  const classSignups = totals.classActionClicks || 0;

  if ((summary.waitlistClasses || 0) > 0) {
    lines.push(
      `${summary.waitlistClasses} classes are full or waitlist in Jackrabbit right now.`
    );
  }

  if ((summary.openSpotTotal || 0) > 0) {
    lines.push(`${summary.openSpotTotal} open spots available across your schedule.`);
  }

  const emptySearchCount = filterUsage.schedule_filter_empty_view || 0;

  if (emptySearchCount > 0) {
    lines.push(
      `${emptySearchCount} famil${emptySearchCount === 1 ? "y" : "ies"} searched for classes and found no matches in the last ${days} days.`
    );
  }

  if (programViews > 0) {
    lines.push(
      `${programViews} families clicked a program on your website in the last ${days} days.`
    );
  }

  if (classSignups > 0) {
    const rate = formatPercent(classSignups, programViews);
    lines.push(
      `${classSignups} clicked Register or Waitlist on a specific class${
        rate !== null ? ` (${rate}% of program browsers)` : ""
      }.`
    );
  }

  if ((totals.waitlistClicks || 0) > 0) {
    lines.push(
      `${totals.waitlistClicks} waitlist clicks on the website — families wanted full classes.`
    );
  }

  if ((totals.generalRegisterClicks || 0) > 0) {
    lines.push(
      `${totals.generalRegisterClicks} used the general Register button (nav or homepage).`
    );
  }

  if ((filterUsage.schedule_filter_view || 0) > 0 && emptySearchCount === 0) {
    lines.push(
      `${filterUsage.schedule_filter_view} families saw the class finder; ${filterUsage.schedule_filter_first_use || 0} tried it.`
    );
  }

  if (lines.length === 0) {
    lines.push(`No website activity recorded in the last ${days} days yet.`);
  }

  return lines;
};

const formatTrendsRangeLabel = (days = 365) => {
  if (days >= 365) return "12 months";
  if (days >= 180) return "6 months";
  if (days >= 90) return "3 months";
  return `${days} days`;
};

const EnrollmentInsights = ({ enrollment, ga4, capacity }) => {
  if (!ga4?.connected || !enrollment) {
    return (
      <section className="owner-dashboard__section owner-dashboard__section--callout">
        <div className="owner-dashboard__section-head">
          <h2>Website analytics</h2>
          <p>
            Website tracking is not connected yet. Connect Google Analytics to
            see how families use your site.
          </p>
        </div>
        {ga4?.error && (
          <p className="owner-dashboard__error">GA4 error: {ga4.error}</p>
        )}
      </section>
    );
  }

  const totals = enrollment.totals || {};
  const filterUsage = enrollment.filterUsage || {};
  const days = enrollment.dateRange?.days || 30;
  const trendsDays = enrollment.websiteTrends?.days || 365;
  const trendsRangeLabel = formatTrendsRangeLabel(trendsDays);
  const quickSummary = buildQuickSummary(totals, filterUsage, capacity, days);

  return (
    <section className="owner-dashboard__panel owner-dashboard__panel--signals">
      <div className="owner-dashboard__panel-header">
        <div>
          <h2 className="owner-dashboard__panel-title">Website analytics</h2>
          <p className="owner-dashboard__panel-subtitle">
            How families find and use ace-gymnastics.com — trend chart looks back up to{" "}
            {trendsRangeLabel}; summary stats below cover the last {days} days.
          </p>
        </div>
      </div>

      <WebsiteTrendsChart websiteTrends={enrollment.websiteTrends} />

      <EmptyClassSearches
        emptyClassSearches={enrollment.emptyClassSearches}
        emptyViewCount={filterUsage.schedule_filter_empty_view || 0}
        days={days}
      />

      <WebsiteUsageSummary
        websiteUsage={enrollment.websiteUsage}
        days={days}
      />

      <TrafficSources
        trafficSources={enrollment.trafficSources}
        days={days}
      />

      <PopularSearches
        searchQueries={enrollment.searchQueries}
        days={days}
      />

      <div className="owner-dashboard__quick-read">
        <h3 className="owner-dashboard__quick-read-title">At a glance</h3>
        <ul>
          {quickSummary.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default EnrollmentInsights;
